import { select } from "redux-saga/effects";
import replayManager from "legacy/workers/replay/ApiDSLReplayManager";
import { selectIsEditingWithAi } from "store/slices/ai/selectors";
import { EntitiesErrorType } from "store/utils/types";
import { fastClone } from "utils/clone";
import { createSaga, forkSagas } from "../../../utils/saga";
import slice from "../slice";
import { hydrateBlockSaga } from "./hydrateBlock";
import { persistV2ApiSaga } from "./persistV2Api";
import type * as BackendTypes from "../backend-types";
import type { ScheduleState } from "@superblocksteam/shared";

export type UpdateV2ApiPayload = {
  apiPb: BackendTypes.Api;
  scheduleState?: ScheduleState;
  hydrateBlockId?: string;
  updated?: Date;
  shouldUpdateUndoStack?: boolean;
};

type UpdateV2ApiResult = {
  shouldUpdateUndoStack?: boolean;
};

function* updateV2Api(
  payload: UpdateV2ApiPayload,
  callId: number,
): Generator<unknown, UpdateV2ApiResult, any> {
  const isEditingWithAi: boolean = yield select(selectIsEditingWithAi);

  if (payload?.hydrateBlockId) {
    yield forkSagas([
      hydrateBlockSaga.apply({
        apiId: payload.apiPb.metadata.id,
        blockId: payload.hydrateBlockId,
        updatingApiCallId: callId,
        isEditingWithAi,
      }),
    ]);
  }

  if (!isEditingWithAi) {
    yield forkSagas([
      persistV2ApiSaga.apply({
        api: payload.apiPb,
        scheduleState: payload.scheduleState,
        updatingApiCallId: callId,
        updated: payload.updated,
      }),
    ]);
  }

  return {
    shouldUpdateUndoStack: payload.shouldUpdateUndoStack,
  };
}

export const updateV2ApiSaga = createSaga(updateV2Api, "updateV2ApiSaga", {
  sliceName: slice.name,
});

slice.saga(updateV2ApiSaga, {
  start(state, { payload }) {
    const apiId = payload.apiPb.metadata.id;
    state.meta[apiId] = state.meta[apiId] ?? {};
    state.meta[apiId].dirty = true;
    state.meta[apiId].needsBindingExtraction = true;

    if (payload.shouldUpdateUndoStack) {
      replayManager.initializeManager(
        apiId,
        fastClone(state.entities[apiId]?.apiPb),
      );
    }

    state.entities[apiId] = {
      ...state.entities[apiId],
      apiPb: payload.apiPb,
      updated: payload.updated,
    };
    state.meta[apiId].isUpdating = true;
    delete state.errors[apiId];
  },
  success(state, { payload, meta }) {
    // TODO, we may want to clear API results depending on the situation.
    // For example, a deleted block should have its results cleared.
    // If it's renamed, the output should be renamed. This is complex though and the responsibility
    // likely does not fall on this success handler like it does for V1 APIs.
    const apiId = meta.args.apiPb.metadata.id;
    state.meta[apiId].isUpdating = false;

    if (payload.shouldUpdateUndoStack) {
      replayManager.update(apiId, fastClone(state.entities[apiId]?.apiPb));
    }
  },
  error(state, { payload, meta }) {
    state.errors[meta.args.apiPb.metadata.id] = {
      type: EntitiesErrorType.SAVE_ERROR,
      error: payload,
    };
    state.meta[meta.args.apiPb.metadata.id].isUpdating = false;
  },
});
